import {
  ArchiveRestore, ArrowDown, ArrowLeft, ArrowUp, Building2, ChevronDown, Folder, FolderInput, FolderPlus,
  Layers, Lock, MoreHorizontal, Pencil, Plus, Search, Trash2, UserRound, Voicemail, type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { CopyButton, Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { AccessSummary } from "@/features/form-access";
import { FormFieldDialog } from "@/features/form-field-dialog";
import { ArchiveSectionDialog, FormSectionDialog } from "@/features/form-section-dialog";
import { CallPager, CallRangeLabel } from "@/features/call-report";
import { SearchableFieldsDialog } from "@/features/searchable-fields-dialog";
import { SpeakableFieldsDialog } from "@/features/speakable-fields-dialog";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  ALL_ROWS, OBJECTS, PAGE_SIZES, counts, fieldRows, folderRows, moveTargets, objectLabel, pageOf, pageSizeOf,
  type Designs,
  type FieldRow, type ObjectFilter,
} from "@/lib/custom-fields";
import { catalogTenant } from "@/lib/dispositions";
import {
  FIELD_TYPES, archiveFieldNote, fieldTypeLabel, folderLevels, layoutFrom, movedFieldLayout,
  movedSectionLayout, recordNoun,
} from "@/lib/forms";
import {
  useFormDesign, useFormLayout, useTenants, useUpdateFormField, useUpdateFormSection,
} from "@/lib/queries";
import type { AdminFormField, AdminFormSection, FormEntity } from "@/lib/types";
import { cn, formatDateTime } from "@/lib/utils";

/**
 * Custom fields (ACL-256, ADR-087 and ADR-088 in `platform`): the fields a contact and a firm carry beyond
 * the platform's own, the folders they are grouped in on the record, and what supervisors and reps may do
 * with each, laid out as GoHighLevel's Custom Fields settings so the people who used that find their way.
 *
 * The object cards pick the contact's form, the firm's or both; Fields and Folders switch the table; a
 * folder opens to its own fields, in their order, which the arrows change. Access is per role on a folder
 * and on a field, and a field's own setting wins over its folder's (`rbac.field_access`): the Access column
 * shows what each role ends up with, a level that comes from the folder drawn quieter.
 *
 * Nothing is deleted. Archiving takes a field or a folder off the record and keeps every answer; Archived
 * lists them for restoring. Everything here is an admin's, on the server too: every route it calls refuses
 * anybody below. The page's state is its URL (`?object=firm&view=folders&folder=intake`), so a link lands
 * where the admin was. A superadmin names the tenant (`?tenant=`), as on the other catalogs.
 */

type View = "fields" | "folders";

const OBJECT_ICON: Record<ObjectFilter, LucideIcon> = { all: Layers, lead: UserRound, firm: Building2 };

export function CustomFieldsPage() {
  const { user: actor } = useAuth();
  const toast = useToast();
  const isSuperadmin = actor?.role === "superadmin";
  const [params, setParams] = useSearchParams();
  const tenants = useTenants({ enabled: isSuperadmin });
  const shownTenant = catalogTenant(actor, tenants.data, Number(params.get("tenant")) || null);
  const tenantId = isSuperadmin ? shownTenant : null;
  const ready = !isSuperadmin || shownTenant !== null;
  const lead = useFormDesign("lead", tenantId, { enabled: ready, refetchOnMount: "always" });
  const firm = useFormDesign("firm", tenantId, { enabled: ready, refetchOnMount: "always" });
  const designs: Designs = { lead: lead.data, firm: firm.data };
  const layout = useFormLayout();
  const updateField = useUpdateFormField();
  const updateSection = useUpdateFormSection();

  const object = (["lead", "firm"].includes(params.get("object") ?? "") ? params.get("object") : "all") as ObjectFilter;
  const view: View = params.get("view") === "folders" ? "folders" : "fields";
  const folderParam = params.get("folder");
  const archived = params.get("archived") === "1";
  const search = params.get("q") ?? "";
  const type = params.get("type");
  const pageParam = Math.max(0, Math.floor(Number(params.get("page") ?? "0")) || 0);
  const sizeParam = params.get("size");
  const pageSize = pageSizeOf(sizeParam);
  const folderEntity: FormEntity | null = object === "all" ? null : object;
  const openFolder =
    folderEntity && folderParam ? designs[folderEntity]?.sections.find((s) => s.key === folderParam) ?? null : null;
  // The fields the filters keep, a page at a time: 100 and more on one page was a scroll, not a list.
  const paged = pageOf(
    fieldRows(designs, {
      object,
      folder: openFolder && folderEntity ? { entity: folderEntity, key: openFolder.key } : null,
      search,
      type,
      archived,
    }),
    pageParam,
    pageSize,
  );

  const setParam = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    // A new search, type, folder, object or view starts from the first page.
    if (!("page" in changes)) next.delete("page");
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const [fieldDialog, setFieldDialog] = React.useState<
    { entity: FormEntity; field?: AdminFormField; section?: string } | null
  >(null);
  const [folderDialog, setFolderDialog] = React.useState<{ entity: FormEntity; section?: AdminFormSection } | null>(null);
  const [archivingFolder, setArchivingFolder] = React.useState<{ entity: FormEntity; section: AdminFormSection } | null>(null);
  const [archivingField, setArchivingField] = React.useState<FieldRow | null>(null);
  const [moving, setMoving] = React.useState<FieldRow | null>(null);
  const [searchableDialog, setSearchableDialog] = React.useState<FormEntity | null>(null);
  const [speakableDialog, setSpeakableDialog] = React.useState<FormEntity | null>(null);

  const all = counts(designs, "all");
  const shownCounts = counts(designs, object);
  const pending = !ready || lead.isPending || firm.isPending;
  const failed = lead.error ?? firm.error;

  const restoreField = (row: FieldRow) =>
    updateField.mutate(
      { entity: row.entity, tenantId, key: row.field.key, archived: false },
      {
        onSuccess: () => toast.success("Field restored", row.field.title),
        onError: (e) => toast.error("Could not restore the field", e instanceof ApiError ? e.message : "Please try again."),
      },
    );
  const restoreFolder = (entity: FormEntity, section: AdminFormSection) =>
    updateSection.mutate(
      { entity, tenantId, key: section.key, archived: false },
      {
        onSuccess: () => toast.success("Folder restored", `${section.title}. Restore its fields one by one if they were archived with it.`),
        onError: (e) => toast.error("Could not restore the folder", e instanceof ApiError ? e.message : "Please try again."),
      },
    );
  const saveOrder = (entity: FormEntity, next: ReturnType<typeof layoutFrom> | null) => {
    if (!next) return;
    layout.mutate(
      { entity, tenantId, sections: next },
      { onError: (e) => toast.error("Could not change the order", e instanceof ApiError ? e.message : "Please try again.") },
    );
  };

  /** Create field / Create folder: straight into the object shown, or a menu of the two when both are. */
  const createButton = (what: "field" | "folder") => {
    const icon = what === "field" ? <Plus /> : <FolderPlus />;
    const label = what === "field" ? "Create field" : "Create folder";
    const open = (entity: FormEntity) =>
      what === "field"
        ? setFieldDialog({ entity, section: openFolder?.key })
        : setFolderDialog({ entity });
    const variant = what === "field" ? "default" : "outline";
    if (object !== "all") {
      return (
        <Button variant={variant} size="sm" onClick={() => open(object)} disabled={pending}>
          {icon}
          {label}
        </Button>
      );
    }
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={variant} size="sm" disabled={pending}>
            {icon}
            {label}
            <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {OBJECTS.map((entity) => (
            <DropdownMenuItem key={entity} onSelect={() => open(entity)}>
              {objectLabel(entity)} {what}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  /** The ⋮ menu, as GoHighLevel's: Edit searchable fields, on the object shown (contacts under All), which
   *  the dialog's own object picker changes (ADR-101). And the fields a voicemail greeting can say (ADR-113),
   *  for the object shown or each of the two under All: that dialog is of one object. */
  const moreMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon-sm" aria-label="More" title="More" disabled={pending}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => setSearchableDialog(object === "all" ? "lead" : object)}>
          Edit searchable fields
        </DropdownMenuItem>
        {(object === "all" ? OBJECTS : [object]).map((entity) => (
          <DropdownMenuItem key={`say-${entity}`} onSelect={() => setSpeakableDialog(entity)}>
            Edit {object === "all" ? `${objectLabel(entity).toLowerCase()} ` : ""}fields a voicemail can say
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <Page>
      <PageHeader
        title="Custom fields"
        description="The fields contacts and firms carry, the folders they are grouped in, and who can see and edit each."
        action={
          isSuperadmin && (tenants.data?.length ?? 0) > 1 ? (
            <Select value={shownTenant?.toString() ?? ""} onValueChange={(v) => setParam({ tenant: v, folder: null })}>
              <SelectTrigger className="w-56" aria-label="Tenant">
                <SelectValue placeholder="Choose a tenant" />
              </SelectTrigger>
              <SelectContent>
                {(tenants.data ?? []).map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : undefined
        }
      />

      <div role="tablist" aria-label="Object" className="mb-4 flex flex-wrap gap-2">
        {(["all", ...OBJECTS] as ObjectFilter[]).map((o) => {
          const Icon = OBJECT_ICON[o];
          const selected = object === o;
          return (
            <button
              key={o}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setParam({ object: o === "all" ? null : o, folder: null })}
              className={cn(
                "inline-flex h-11 items-center gap-2 rounded-lg border px-4 text-sm shadow-xs transition-colors",
                selected
                  ? "border-accent-7 bg-accent-2 text-accent-12"
                  : "border-border bg-surface text-text hover:bg-surface-hover",
              )}
            >
              <Icon className={cn("size-4", selected ? "text-accent-11" : "text-text-subtle")} aria-hidden />
              {o === "all" ? "All" : objectLabel(o)}
              <Badge tone={selected ? "accent" : "neutral"} size="sm">
                {pending ? "…" : o === "all" ? all.fields : counts(designs, o).fields}
              </Badge>
            </button>
          );
        })}
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          {openFolder && folderEntity ? (
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Button variant="ghost" size="icon-sm" aria-label="Back to the folders" onClick={() => setParam({ folder: null })}>
                <ArrowLeft />
              </Button>
              <Folder className="size-4 text-text-subtle" aria-hidden />
              <h2 className="truncate text-base font-semibold text-text">{openFolder.title}</h2>
              <Badge tone="neutral" size="sm">{openFolder.fields.filter((f) => !f.archived).length}</Badge>
              <AccessSummary levels={folderLevels(openFolder.rules)} />
              <Button variant="ghost" size="sm" onClick={() => setFolderDialog({ entity: folderEntity, section: openFolder })}>
                <Pencil />
                Edit folder
              </Button>
            </div>
          ) : (
            <div role="tablist" aria-label="List" className="inline-flex rounded-md border border-border p-0.5">
              {(["fields", "folders"] as View[]).map((v) => (
                <button
                  key={v}
                  type="button"
                  role="tab"
                  aria-selected={view === v}
                  onClick={() => setParam({ view: v === "fields" ? null : v, type: null })}
                  className={cn(
                    "inline-flex h-7 items-center gap-1.5 rounded px-3 text-sm transition-colors",
                    view === v ? "bg-accent-3 text-accent-11" : "text-text-muted hover:text-text",
                  )}
                >
                  {v === "fields" ? "Fields" : "Folders"}
                  <span className="text-xs tabular-nums opacity-80">
                    {pending ? "…" : v === "fields" ? shownCounts.fields : shownCounts.folders}
                  </span>
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {!openFolder && createButton("folder")}
            {createButton("field")}
            {moreMenu()}
          </div>
        </div>

        <Toolbar>
          <div className="w-full sm:w-64">
            <Input
              leading={<Search />}
              placeholder={view === "folders" && !openFolder ? "Search folders" : "Search fields"}
              aria-label="Search"
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParam({ q: e.target.value })}
            />
          </div>
          {(view === "fields" || openFolder) && (
            <Select value={type ?? "any"} onValueChange={(v) => setParam({ type: v === "any" ? null : v })}>
              <SelectTrigger className="w-40" aria-label="Field type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any type</SelectItem>
                {FIELD_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {fieldTypeLabel(t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="ml-auto flex items-center gap-3">
            {(view === "fields" || openFolder) && !pending && !failed && (
              <>
                <span className="text-xs tabular-nums text-text-muted">
                  <CallRangeLabel from={paged.page * (Number.isFinite(pageSize) ? pageSize : 0)} count={paged.rows.length} total={paged.total} />
                </span>
                <Select value={sizeParam ?? String(PAGE_SIZES[0])} onValueChange={(v) => setParam({ size: v === String(PAGE_SIZES[0]) ? null : v })}>
                  <SelectTrigger className="w-32" aria-label="Rows per page">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} per page
                      </SelectItem>
                    ))}
                    <SelectItem value={ALL_ROWS}>All on one page</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
            <Switch id="custom-fields-archived" checked={archived} onCheckedChange={(on) => setParam({ archived: on ? "1" : null })} />
            <label htmlFor="custom-fields-archived" className="cursor-pointer text-xs text-text-muted">
              Archived
            </label>
          </div>
        </Toolbar>

        {isSuperadmin && tenants.isPending ? (
          <TableSkeleton cols={6} />
        ) : !ready ? (
          <EmptyState icon={Layers} title="No tenant to show" description="Custom fields belong to a tenant, and there is none to read them from." />
        ) : pending ? (
          <TableSkeleton cols={6} />
        ) : failed ? (
          <ErrorState
            error={failed}
            title="Could not load the custom fields"
            action={
              <Button variant="outline" onClick={() => { void lead.refetch(); void firm.refetch(); }}>
                Try again
              </Button>
            }
          />
        ) : view === "folders" && !openFolder ? (
          <FoldersTable
            designs={designs}
            object={object}
            search={search}
            archived={archived}
            onOpen={(entity, section) => setParam({ object: entity, folder: section.key, view: null, q: null, type: null })}
            onEdit={(entity, section) => setFolderDialog({ entity, section })}
            onArchive={(entity, section) => setArchivingFolder({ entity, section })}
            onRestore={restoreFolder}
            onMove={(entity, key, by) => saveOrder(entity, movedSectionLayout(designs[entity]?.sections ?? [], key, by))}
            onCreate={() => (object === "all" ? undefined : setFolderDialog({ entity: object }))}
          />
        ) : (
          <>
            <FieldsTable
              rows={paged.rows}
              offset={paged.page * (Number.isFinite(pageSize) ? pageSize : 0)}
              total={paged.total}
              showObject={object === "all"}
              inFolder={Boolean(openFolder)}
              canReorder={Boolean(openFolder) && !search && !type && !archived}
              filtered={Boolean(search || type)}
              archived={archived}
              onEdit={(row) => setFieldDialog({ entity: row.entity, field: row.field, section: row.section.key })}
              onOpenFolder={(row) => setParam({ object: row.entity, folder: row.section.key, view: null, q: null, type: null })}
              onMove={setMoving}
              onArchive={setArchivingField}
              onRestore={restoreField}
              onReorder={(row, by) =>
                saveOrder(row.entity, movedFieldLayout(designs[row.entity]?.sections ?? [], row.section.key, row.field.key, by))
              }
            />
            <CallPager
              page={paged.page}
              count={paged.rows.length}
              total={paged.total}
              pageSize={pageSize}
              busy={false}
              onChange={(next) => setParam({ page: next > 0 ? String(next) : null })}
            />
          </>
        )}
      </Card>

      {fieldDialog && (
        <FormFieldDialog
          open
          onOpenChange={(open) => !open && setFieldDialog(null)}
          field={fieldDialog.field}
          sectionKey={fieldDialog.section}
          form={designs[fieldDialog.entity]}
          entity={fieldDialog.entity}
          tenantId={tenantId}
          forms={designs}
        />
      )}
      {searchableDialog && (
        <SearchableFieldsDialog
          open
          onOpenChange={(open) => !open && setSearchableDialog(null)}
          entity={searchableDialog}
          tenantId={tenantId}
        />
      )}
      {speakableDialog && (
        <SpeakableFieldsDialog
          open
          onOpenChange={(open) => !open && setSpeakableDialog(null)}
          entity={speakableDialog}
          form={designs[speakableDialog]}
          tenantId={tenantId}
        />
      )}
      {folderDialog && (
        <FormSectionDialog
          open
          onOpenChange={(open) => !open && setFolderDialog(null)}
          section={folderDialog.section}
          form={designs[folderDialog.entity]}
          entity={folderDialog.entity}
          tenantId={tenantId}
        />
      )}
      {archivingFolder && (
        <ArchiveSectionDialog
          section={archivingFolder.section}
          sections={designs[archivingFolder.entity]?.sections ?? []}
          entity={archivingFolder.entity}
          tenantId={tenantId}
          onOpenChange={(open) => {
            if (open) return;
            if (openFolder?.key === archivingFolder.section.key) setParam({ folder: null });
            setArchivingFolder(null);
          }}
        />
      )}
      <ConfirmDialog
        open={archivingField !== null}
        onOpenChange={(open) => !open && setArchivingField(null)}
        title={`Archive ${archivingField?.field.title ?? "the field"}?`}
        description={
          archivingField
            ? `It leaves every ${recordNoun(archivingField.entity)}'s record. ${archiveFieldNote(archivingField.field.values, recordNoun(archivingField.entity))}`
            : ""
        }
        confirmLabel="Archive field"
        loading={updateField.isPending}
        onConfirm={() =>
          archivingField &&
          updateField.mutate(
            { entity: archivingField.entity, tenantId, key: archivingField.field.key, archived: true },
            {
              onSuccess: () => {
                toast.success("Field archived", archivingField.field.title);
                setArchivingField(null);
              },
              onError: (e) => toast.error("Could not archive the field", e instanceof ApiError ? e.message : "Please try again."),
            },
          )
        }
      />
      <MoveFieldDialog row={moving} designs={designs} tenantId={tenantId} onClose={() => setMoving(null)} />
    </Page>
  );
}

/** The day it was made in the reader's own way of writing dates, the full moment on hover; a dash for a
 *  row the server sent no date for. */
function Created({ at }: { at?: string | null }) {
  const d = at ? new Date(at) : null;
  if (!d || Number.isNaN(d.getTime())) return <span className="text-text-subtle">—</span>;
  return (
    <span title={formatDateTime(at)}>
      {d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
    </span>
  );
}

function IconAction({
  label, icon: Icon, onClick, disabled,
}: {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <Icon />
    </Button>
  );
}

function FieldsTable({
  rows, offset, total, showObject, inFolder, canReorder, filtered, archived, onEdit, onOpenFolder, onMove, onArchive,
  onRestore, onReorder,
}: {
  /** This page's rows, `offset` rows into the `total` the filters keep. */
  rows: FieldRow[];
  offset: number;
  total: number;
  showObject: boolean;
  inFolder: boolean;
  canReorder: boolean;
  filtered: boolean;
  archived: boolean;
  onEdit: (row: FieldRow) => void;
  onOpenFolder: (row: FieldRow) => void;
  onMove: (row: FieldRow) => void;
  onArchive: (row: FieldRow) => void;
  onRestore: (row: FieldRow) => void;
  onReorder: (row: FieldRow, by: -1 | 1) => void;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title={archived ? "Nothing archived" : filtered ? "No field matches" : inFolder ? "No fields in this folder" : "No fields yet"}
        description={
          archived
            ? "Archived fields and folders are listed here, with their answers kept, ready to restore."
            : filtered
              ? "Try another search or type."
              : "Create a field: it appears on every record of its object, in its folder, for the roles that may see it."
        }
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Field name</TableHead>
          <TableHead>Type</TableHead>
          {showObject && <TableHead>Object</TableHead>}
          {!inFolder && <TableHead>Folder</TableHead>}
          <TableHead>Key</TableHead>
          <TableHead>Access</TableHead>
          <TableHead className="text-right">In use</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => {
          const { field, section, entity } = row;
          const key = field.key;
          return (
            <TableRow key={`${entity}:${field.key}`}>
              <TableCell className="min-w-48">
                <span className="inline-flex items-center gap-1.5">
                  <button type="button" className="text-left font-medium hover:underline" onClick={() => onEdit(row)}>
                    {field.title}
                  </button>
                  {field.system && (
                    <Lock className="size-3.5 text-text-subtle" aria-label="One of the platform's own fields" />
                  )}
                  {field.required && <Badge tone="outline" size="sm">Required</Badge>}
                  {field.searchable && (
                    <Badge tone="accent" size="sm">
                      <Search className="size-3" aria-hidden />
                      Searchable
                    </Badge>
                  )}
                  {field.speakable && (
                    <Badge tone="accent" size="sm">
                      <Voicemail className="size-3" aria-hidden />
                      Spoken in voicemails
                    </Badge>
                  )}
                </span>
              </TableCell>
              <TableCell className="whitespace-nowrap text-text-muted">{fieldTypeLabel(field.type)}</TableCell>
              {showObject && <TableCell className="whitespace-nowrap text-text-muted">{objectLabel(entity)}</TableCell>}
              {!inFolder && (
                <TableCell>
                  <button
                    type="button"
                    onClick={() => onOpenFolder(row)}
                    className="inline-flex max-w-44 items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs text-text hover:bg-surface-hover"
                  >
                    <Folder className="size-3 shrink-0 text-text-subtle" aria-hidden />
                    <span className="truncate">{section.title}</span>
                  </button>
                </TableCell>
              )}
              <TableCell>
                <span className="inline-flex items-center gap-1">
                  <code className="max-w-44 truncate font-mono text-xs text-text-muted" title={key}>{key}</code>
                  <CopyButton value={key} label={`Copy ${field.title}'s key`} />
                </span>
              </TableCell>
              <TableCell>
                <AccessSummary levels={row.levels} inherited={row.inherited} />
              </TableCell>
              <TableCell className="text-right tabular-nums text-text-muted">
                {field.system ? (
                  // Its values live on the record itself, not in the form's values, so the count says nothing.
                  <span title={`Stored on the ${recordNoun(row.entity)} itself`}>—</span>
                ) : (
                  field.values.toLocaleString("en-US")
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs text-text-muted"><Created at={field.created_at} /></TableCell>
              <TableCell className="whitespace-nowrap text-right">
                {canReorder && (
                  <>
                    <IconAction label={`Move ${field.title} up`} icon={ArrowUp} disabled={offset + i === 0} onClick={() => onReorder(row, -1)} />
                    <IconAction label={`Move ${field.title} down`} icon={ArrowDown} disabled={offset + i === total - 1} onClick={() => onReorder(row, 1)} />
                  </>
                )}
                {archived ? (
                  <IconAction label={`Restore ${field.title}`} icon={ArchiveRestore} onClick={() => onRestore(row)} />
                ) : (
                  <>
                    <IconAction label={`Edit ${field.title}`} icon={Pencil} onClick={() => onEdit(row)} />
                    <IconAction label={`Move ${field.title} to another folder`} icon={FolderInput} onClick={() => onMove(row)} />
                    <IconAction
                      label={field.system ? "The platform's own fields cannot be archived" : `Archive ${field.title}`}
                      icon={Trash2}
                      disabled={field.system}
                      onClick={() => onArchive(row)}
                    />
                  </>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function FoldersTable({
  designs, object, search, archived, onOpen, onEdit, onArchive, onRestore, onMove, onCreate,
}: {
  designs: Designs;
  object: ObjectFilter;
  search: string;
  archived: boolean;
  onOpen: (entity: FormEntity, section: AdminFormSection) => void;
  onEdit: (entity: FormEntity, section: AdminFormSection) => void;
  onArchive: (entity: FormEntity, section: AdminFormSection) => void;
  onRestore: (entity: FormEntity, section: AdminFormSection) => void;
  onMove: (entity: FormEntity, key: string, by: -1 | 1) => void;
  onCreate: () => void;
}) {
  const rows = folderRows(designs, { object, search, archived });
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Folder}
        title={archived ? "No archived folders" : search ? "No folder matches" : "No folders yet"}
        description={
          archived || search
            ? undefined
            : "A folder groups fields into one card on the record, and sets who can see and edit them."
        }
        action={
          !archived && !search && object !== "all" ? (
            <Button variant="outline" size="sm" onClick={onCreate}>
              <FolderPlus />
              Create folder
            </Button>
          ) : undefined
        }
      />
    );
  }
  // The order only means something within one object's form, and with nothing narrowed.
  const canReorder = object !== "all" && !search && !archived;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Folder name</TableHead>
          {object === "all" && <TableHead>Object</TableHead>}
          <TableHead className="text-right">Fields</TableHead>
          <TableHead>Access</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow
            key={`${row.entity}:${row.section.key}`}
            interactive={!archived}
            tabIndex={archived ? undefined : 0}
            onClick={archived ? undefined : () => onOpen(row.entity, row.section)}
            onKeyDown={(e) => {
              if (!archived && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onOpen(row.entity, row.section);
              }
            }}
          >
            <TableCell className="min-w-48">
              <span className="inline-flex items-center gap-2 font-medium">
                <Folder className="size-4 text-text-subtle" aria-hidden />
                {row.section.title}
              </span>
            </TableCell>
            {object === "all" && <TableCell className="text-text-muted">{objectLabel(row.entity)}</TableCell>}
            <TableCell className="text-right tabular-nums">{row.fields}</TableCell>
            <TableCell>
              <AccessSummary levels={row.levels} />
            </TableCell>
            <TableCell className="whitespace-nowrap text-xs text-text-muted"><Created at={row.section.created_at} /></TableCell>
            <TableCell className="whitespace-nowrap text-right">
              {canReorder && (
                <>
                  <IconAction label={`Move ${row.section.title} up`} icon={ArrowUp} disabled={i === 0} onClick={() => onMove(row.entity, row.section.key, -1)} />
                  <IconAction label={`Move ${row.section.title} down`} icon={ArrowDown} disabled={i === rows.length - 1} onClick={() => onMove(row.entity, row.section.key, 1)} />
                </>
              )}
              {archived ? (
                <IconAction label={`Restore ${row.section.title}`} icon={ArchiveRestore} onClick={() => onRestore(row.entity, row.section)} />
              ) : (
                <>
                  <IconAction label={`Edit ${row.section.title}`} icon={Pencil} onClick={() => onEdit(row.entity, row.section)} />
                  <IconAction label={`Archive ${row.section.title}`} icon={Trash2} onClick={() => onArchive(row.entity, row.section)} />
                </>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Move to folder: another active folder of the same object. The field goes last there. */
function MoveFieldDialog({
  row, designs, tenantId, onClose,
}: {
  row: FieldRow | null;
  designs: Designs;
  tenantId: number | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const update = useUpdateFormField();
  const [to, setTo] = React.useState("");
  const [problem, setProblem] = React.useState<string | null>(null);
  const targets = row ? moveTargets(designs[row.entity], row.section.key) : [];
  React.useEffect(() => {
    setTo("");
    setProblem(null);
  }, [row?.field.key]);
  if (!row) return null;
  const target = targets.find((s) => s.key === to);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Move {row.field.title}</DialogTitle>
          <DialogDescription>
            It goes last in the folder you pick, and keeps its answers and its own access settings.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {targets.length === 0 ? (
            <p className="text-sm text-text-muted">There is no other folder for {objectLabel(row.entity).toLowerCase()}s yet.</p>
          ) : (
            <Field label="Folder" htmlFor="move-field-to" error={problem ?? undefined}>
              <Select value={to} onValueChange={(v) => { setTo(v); setProblem(null); }}>
                <SelectTrigger id="move-field-to" aria-label="Folder">
                  <SelectValue placeholder="Pick a folder" />
                </SelectTrigger>
                <SelectContent>
                  {targets.map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            loading={update.isPending}
            disabled={!to}
            onClick={() =>
              update.mutate(
                { entity: row.entity, tenantId, key: row.field.key, section: to },
                {
                  onSuccess: () => {
                    toast.success("Field moved", `${row.field.title} is now in ${target?.title ?? to}.`);
                    onClose();
                  },
                  onError: (e) => setProblem(e instanceof ApiError ? e.message : "Could not move the field."),
                },
              )
            }
          >
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
